/**
 * 🧭 NAVEGACIÓN UNIFICADA MGI
 * Sistema centralizado de navegación por roles
 */

import { Router } from 'vue-router'
import { computed } from 'vue'
import { UnifiedRoleService, UNIFIED_ROLES, useUnifiedRoles } from './unified-roles.service'

// 🎯 ESTRUCTURA DE NAVEGACIÓN POR ROL
export const ROLE_NAVIGATION = {
  SUPER_ADMIN: {
    mainMenu: [
      {
        title: 'Dashboard Principal',
        icon: 'mdi-view-dashboard',
        route: '/super-admin',
        permission: 'system:admin'
      },
      {
        title: 'Gestión de Usuarios',
        icon: 'mdi-account-multiple',
        route: '/users',
        permission: 'users:admin',
        children: [
          { title: 'Lista de Usuarios', route: '/users' },
          { title: 'Crear Usuario', route: '/users' },
          { title: 'Roles y Permisos', route: '/users' }
        ]
      },
      {
        title: 'Gestión de Grupos',
        icon: 'mdi-account-group',
        route: '/super-admin/groups-management',
        permission: 'groups:admin',
        children: [
          { title: 'Gestión de Grupos', route: '/super-admin/groups-management' },
          { title: 'Gestión de Agencias', route: '/super-admin/agencies-management' },
          { title: 'Vista Consolidada', route: '/super-admin/dashboard-consolidated' }
        ]
      },
      {
        title: 'Gestión Departamental',
        icon: 'mdi-office-building',
        route: '/management/departments',
        permission: 'system:admin',
        children: [
          { title: 'Departamentos', route: '/management/departments' },
          { title: 'Usuario-Departamentos', route: '/management/users-departments' },
          { title: 'Estructura Departamental', route: '/departments' },
          { title: 'Asignaciones', route: '/assignments' },
          { title: 'Gestión de Asignaciones', route: '/assignments/management' }
        ]
      },
      {
        title: 'Inventario Integral',
        icon: 'mdi-package-variant',
        route: '/inventory',
        permission: 'system:admin',
        children: [
          { title: 'Vista General', route: '/inventory' },
          { title: 'Solicitudes', route: '/inventory/requests' },
          { title: 'Órdenes de salida', route: '/inventory/outbound' }
          // P13-V2B (V2B-PATH-02): las entradas Workspaces / Analytics Workspaces fueron
          // RETIRADAS — sus rutas no existen desde V2A (PD-V2-01).
        ]
      },
      {
        title: 'Mapas Inteligentes',
        icon: 'mdi-map-marker-path',
        route: '/maps',
        permission: 'system:admin',
        children: [
          { title: 'Panel de Mapas', route: '/maps' },
          { title: 'Mapa Interactivo', route: '/maps/interactive' },
          { title: 'Monitoreo en Tiempo Real', route: '/maps/monitoring' }
        ]
      },
      {
        title: 'Analytics Avanzado',
        icon: 'mdi-chart-line',
        route: '/analytics',
        permission: 'analytics:admin',
        children: [
          { title: 'Vista General', route: '/analytics' },
          { title: 'Business Intelligence', route: '/business-intelligence' },
          { title: 'Analytics Avanzados', route: '/analytics/advanced' }
        ]
      },
      {
        title: 'Reportes Corporativos',
        icon: 'mdi-chart-box-multiple',
        route: '/reports',
        permission: 'reports:admin',
        children: [
          { title: 'Centro de Reportes', route: '/reports' },
          { title: 'Reportes Ejecutivos', route: '/reports/executive' },
          { title: 'Reportes Tiempo Real', route: '/reports/realtime' },
          { title: 'Suite Analítica', route: '/reports/analytics-suite' },
          { title: 'Reporte Group Manager', route: '/reports/group-manager' }
        ]
      },
      {
        title: 'Mi Agenda',
        icon: 'mdi-calendar-clock',
        route: '/agenda/mia',
        permission: 'events:view',
        children: [
          { title: 'Mi Agenda', route: '/agenda/mia' },
          { title: 'Bajo mi Responsabilidad', route: '/agenda/responsabilidad' },
          { title: 'Delegadas', route: '/agenda/delegadas' }
        ]
      },
      {
        title: 'Configuración Sistema',
        icon: 'mdi-cog',
        route: '/dev-center',
        permission: 'system:admin',
        children: [
          { title: 'Dev Center', route: '/dev-center' },
          { title: 'API Testing', route: '/dev-center/api-testing' },
          { title: 'Performance Metrics', route: '/dev-center/performance-metrics' }
        ]
      }
    ],
    quickActions: [
      { title: 'Crear Usuario', route: '/users', icon: 'mdi-account-plus' },
      { title: 'Nuevo Grupo', route: '/super-admin/groups-management', icon: 'mdi-folder-plus' },
      { title: 'Ver Reportes', route: '/reports', icon: 'mdi-file-chart' }
    ]
  },
  
  GROUP_MANAGER: {
    mainMenu: [
      {
        title: 'Dashboard Manager',
        icon: 'mdi-view-dashboard',
        route: '/dashboard/manager',
        permission: 'groups:manage'
      },
      {
        title: 'Mi Grupo',
        icon: 'mdi-folder-account',
        route: '/group/manage',
        permission: 'groups:manage',
        children: [
          { title: 'Información del Grupo', route: '/group/info' },
          { title: 'Agencias Asignadas', route: '/group/agencies' },
          { title: 'Miembros del Equipo', route: '/group/members' }
        ]
      },
      {
        title: 'Gestión de Incidencias',
        icon: 'mdi-alert-circle',
        route: '/incidents',
        permission: 'incidents:manage',
        children: [
          { title: 'Incidencias Activas', route: '/incidents/active' },
          { title: 'Historial', route: '/incidents/history' },
          { title: 'Crear Incidencia', route: '/incidents/create' }
        ]
      },
      {
        title: 'Reportes',
        icon: 'mdi-file-chart',
        route: '/reports',
        permission: 'reports:view',
        children: [
          { title: 'Rendimiento del Grupo', route: '/reports/group' },
          { title: 'Estadísticas', route: '/reports/stats' },
          { title: 'Exportar Datos', route: '/reports/export' }
        ]
      },
      {
        title: 'Mi Agenda',
        icon: 'mdi-calendar-clock',
        route: '/agenda/mia',
        permission: 'events:view',
        children: [
          { title: 'Mi Agenda', route: '/agenda/mia' },
          { title: 'Bajo mi Responsabilidad', route: '/agenda/responsabilidad' },
          { title: 'Delegadas', route: '/agenda/delegadas' }
        ]
      }
    ],
    quickActions: [
      { title: 'Nueva Incidencia', route: '/incidents/create', icon: 'mdi-plus-circle' },
      { title: 'Ver Agencias', route: '/group/agencies', icon: 'mdi-office-building' },
      { title: 'Reporte Rápido', route: '/reports/quick', icon: 'mdi-chart-bar' }
    ]
  },
  
  FIELD_SUPERVISOR: {
    mainMenu: [
      {
        title: 'Dashboard Supervisor',
        icon: 'mdi-view-dashboard',
        route: '/dashboard/supervisor',
        permission: 'incidents:supervise'
      },
      {
        title: 'Supervisión de Campo',
        icon: 'mdi-map-marker-radius',
        route: '/field',
        permission: 'incidents:supervise',
        children: [
          { title: 'Rutas Activas', route: '/field/routes' },
          { title: 'Ubicaciones en Tiempo Real', route: '/field/tracking' },
          { title: 'Alertas de Campo', route: '/field/alerts' }
        ]
      },
      {
        title: 'Gestión de Rutas',
        icon: 'mdi-map',
        route: '/routes',
        permission: 'routes:supervise',
        children: [
          { title: 'Planificación', route: '/routes/planning' },
          { title: 'Asignaciones', route: '/routes/assignments' },
          { title: 'Optimización', route: '/routes/optimization' }
        ]
      },
      {
        title: 'Reportes de Campo',
        icon: 'mdi-clipboard-text',
        route: '/reports/field',
        permission: 'reports:create'
      },
      {
        title: 'Mi Agenda',
        icon: 'mdi-calendar-clock',
        route: '/agenda/mia',
        permission: 'events:view',
        children: [
          { title: 'Mi Agenda', route: '/agenda/mia' },
          { title: 'Bajo mi Responsabilidad', route: '/agenda/responsabilidad' },
          { title: 'Delegadas', route: '/agenda/delegadas' }
        ]
      }
    ],
    quickActions: [
      { title: 'Ver Mapa', route: '/field/tracking', icon: 'mdi-map' },
      { title: 'Crear Ruta', route: '/routes/create', icon: 'mdi-plus' },
      { title: 'Alertas', route: '/field/alerts', icon: 'mdi-bell' }
    ]
  },
  
  ROUTE_OPERATOR: {
    mainMenu: [
      {
        title: 'Mi Dashboard',
        icon: 'mdi-view-dashboard',
        route: '/dashboard/operator',
        permission: 'routes:operate'
      },
      {
        title: 'Mi Ruta Activa',
        icon: 'mdi-map-marker-path',
        route: '/route/active',
        permission: 'routes:operate',
        children: [
          { title: 'Puntos de Control', route: '/route/checkpoints' },
          { title: 'Evidencias', route: '/route/evidence' },
          { title: 'Estado de Ruta', route: '/route/status' }
        ]
      },
      {
        title: 'Reportar Incidencia',
        icon: 'mdi-alert',
        route: '/incidents/report',
        permission: 'incidents:report'
      },
      {
        title: 'Mi Historial',
        icon: 'mdi-history',
        route: '/operator/history',
        permission: 'routes:operate'
      },
      {
        title: 'Mi Agenda',
        icon: 'mdi-calendar-clock',
        route: '/agenda/mia',
        permission: 'events:view',
        children: [
          { title: 'Mi Agenda', route: '/agenda/mia' },
          { title: 'Bajo mi Responsabilidad', route: '/agenda/responsabilidad' },
          { title: 'Delegadas', route: '/agenda/delegadas' }
        ]
      }
    ],
    quickActions: [
      { title: 'Iniciar Ruta', route: '/route/start', icon: 'mdi-play' },
      { title: 'Reportar', route: '/incidents/report', icon: 'mdi-alert-plus' },
      { title: 'Check-in', route: '/route/checkin', icon: 'mdi-map-marker-check' }
    ]
  }
} as const

// P13-K3 · `NavigationGuard` lived here and was never registered: nothing in the repository ever
// constructed it or called `setupGlobalGuard`, and it read `auth_token` / `user_role` from
// localStorage — keys the Pinia store never writes (it uses `mgi_auth_token` / `mgi_user_data`).
// A second, dead route guard reading a nonexistent session is a standing invitation to "fix" it
// into life next to the real one, so it is removed rather than left dormant. The single live guard
// is `router.beforeEach` in `src/router/index.ts`, delegating to `canonicalGuard`.

// 🧭 SERVICIO DE NAVEGACIÓN
export class UnifiedNavigationService {
  /**
   * Obtener menú principal para rol
   */
  static getMainMenu(role: string) {
    const config = UnifiedRoleService.getRoleConfig(role)
    if (!config) return []
    
    const roleKey = Object.keys(UNIFIED_ROLES).find(
      key => UNIFIED_ROLES[key as keyof typeof UNIFIED_ROLES].backend === role ||
             UNIFIED_ROLES[key as keyof typeof UNIFIED_ROLES].frontend === role
    ) as keyof typeof ROLE_NAVIGATION | undefined
    
    if (!roleKey || !ROLE_NAVIGATION[roleKey]) return []
    
    return ROLE_NAVIGATION[roleKey].mainMenu.filter(item => 
      UnifiedRoleService.hasPermission(role, item.permission)
    )
  }
  
  /**
   * Obtener acciones rápidas para rol
   */
  static getQuickActions(role: string) {
    const config = UnifiedRoleService.getRoleConfig(role)
    if (!config) return []
    
    const roleKey = Object.keys(UNIFIED_ROLES).find(
      key => UNIFIED_ROLES[key as keyof typeof UNIFIED_ROLES].backend === role ||
             UNIFIED_ROLES[key as keyof typeof UNIFIED_ROLES].frontend === role
    ) as keyof typeof ROLE_NAVIGATION | undefined
    
    if (!roleKey || !ROLE_NAVIGATION[roleKey]) return []
    
    return ROLE_NAVIGATION[roleKey].quickActions
  }
  
  /**
   * Generar breadcrumb para ruta actual
   */
  static generateBreadcrumb(currentRoute: string, userRole: string): Array<{ title: string, route?: string }> {
    const segments = currentRoute.split('/').filter(Boolean)
    const breadcrumb = [{ title: 'Inicio', route: '/' }]
    
    let currentPath = ''
    for (const segment of segments) {
      currentPath += `/${segment}`
      
      // Buscar en el menú del rol actual
      const menu = this.getMainMenu(userRole)
      const menuItem = this.findMenuItem(menu, currentPath)
      
      if (menuItem) {
        breadcrumb.push({
          title: menuItem.title,
          route: currentPath
        })
      } else {
        // Título genérico basado en el segmento
        breadcrumb.push({
          title: this.formatSegmentTitle(segment),
          route: currentPath
        })
      }
    }
    
    return breadcrumb
  }
  
  /**
   * Buscar item de menú por ruta
   */
  private static findMenuItem(menu: any[], route: string): any {
    for (const item of menu) {
      if (item.route === route) return item
      if (item.children) {
        const found = this.findMenuItem(item.children, route)
        if (found) return found
      }
    }
    return null
  }
  
  /**
   * Formatear título de segmento
   */
  private static formatSegmentTitle(segment: string): string {
    return segment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }
}

// 🎯 COMPOSABLE PARA NAVEGACIÓN (Vue)
export function useUnifiedNavigation() {
  const { currentRole } = useUnifiedRoles()
  
  const mainMenu = computed(() => 
    currentRole.value ? UnifiedNavigationService.getMainMenu(currentRole.value) : []
  )
  
  const quickActions = computed(() =>
    currentRole.value ? UnifiedNavigationService.getQuickActions(currentRole.value) : []
  )
  
  const generateBreadcrumb = (route: string) =>
    currentRole.value ? UnifiedNavigationService.generateBreadcrumb(route, currentRole.value) : []
  
  return {
    mainMenu,
    quickActions,
    generateBreadcrumb
  }
}
